import { useCallback } from "react";
import { Button } from "@mui/material";
import { Braces, Copy, Table2 } from "lucide-react";
import { toast } from "react-toastify";
import FacetResultTable from "./FacetResultTable";
import { copyFacetTableRich } from "./facetResultTableClipboard";
import { isMatrixTableData, type FacetTableData } from "./facet-table-helpers";
import FacetViewJsonToggle from "../../tars-charts/FacetViewJsonToggle";
import FacetResultSourceToggle, { type FacetResultDataSource } from "./FacetResultSourceToggle";

interface FacetTableModePanelProps {
    tableData: FacetTableData | null;
    normalizationTableData?: FacetTableData | null;
    resultDataSource: FacetResultDataSource;
    onResultDataSource: (source: FacetResultDataSource) => void;
    tableSubMode: "view" | "json";
    onTableSubMode: (mode: "view" | "json") => void;
    tableJsonDraft: string;
    onTableJsonDraft: (v: string) => void;
    onResetTableJson: () => void;
    onSaveTableJson: () => void;
    onRowOrderChange?: (rowLabels: string[]) => void;
    onColumnOrderChange?: (columnHeaders: string[]) => void;
    sortOrderPersistDisabled?: boolean;
    onSaveTableEdits?: (table: FacetTableData) => void | Promise<void>;
    isSavingTableEdits?: boolean;
}

export default function FacetTableModePanel({
    tableData,
    normalizationTableData,
    resultDataSource,
    onResultDataSource,
    tableSubMode,
    onTableSubMode,
    tableJsonDraft,
    onTableJsonDraft,
    onResetTableJson,
    onSaveTableJson,
    onRowOrderChange,
    onColumnOrderChange,
    sortOrderPersistDisabled,
    onSaveTableEdits,
    isSavingTableEdits = false,
}: FacetTableModePanelProps) {
    const visibleTable =
        resultDataSource === "normalized" && normalizationTableData
            ? normalizationTableData
            : tableData;
    const allowColumnReorder = isMatrixTableData(visibleTable);

    const handleCopyTable = useCallback(async () => {
        if (!visibleTable) return;
        try {
            await copyFacetTableRich(visibleTable);
            toast.success("Table copied with formatting — paste into Word, Gmail, Google Docs, or Outlook");
        } catch {
            toast.error("Could not copy to clipboard");
        }
    }, [visibleTable]);

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 bg-white/80 px-4 py-3 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <p className="text-[12px] font-medium text-slate-500">Data output</p>
                    {normalizationTableData && tableSubMode === "view" ? (
                        <FacetResultSourceToggle
                            value={resultDataSource}
                            onChange={onResultDataSource}
                        />
                    ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <FacetViewJsonToggle
                        mode={tableSubMode}
                        onMode={onTableSubMode}
                        viewLabel="Table view"
                        jsonLabel="Table JSON"
                        ViewIcon={Table2}
                        JsonIcon={Braces}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!visibleTable}
                        onClick={handleCopyTable}
                        startIcon={<Copy size={14} strokeWidth={2} />}
                        title="Copy HTML table (colors) and tab-separated text"
                    >
                        Copy table
                    </Button>
                </div>
            </div>
            {tableSubMode === "view" ? (
                visibleTable ? (
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
                        <FacetResultTable
                            table={visibleTable}
                            onRowOrderChange={onRowOrderChange}
                            onColumnOrderChange={
                                allowColumnReorder ? onColumnOrderChange : undefined
                            }
                            sortOrderPersistDisabled={sortOrderPersistDisabled}
                            allowColumnReorder={allowColumnReorder}
                            onBatchSave={onSaveTableEdits}
                            isSaving={isSavingTableEdits}
                        />
                    </div>
                ) : (
                    <div className="flex flex-1 items-center justify-center px-8 py-16 text-[13px] font-medium text-slate-400">
                        No table is present
                    </div>
                )
            ) : (
                <div className="flex min-h-0 flex-1 flex-col bg-slate-50/50">
                    <textarea
                        value={tableJsonDraft}
                        onChange={(e) => onTableJsonDraft(e.target.value)}
                        spellCheck={false}
                        placeholder="{}"
                        className="min-h-0 flex-1 resize-none border-0 bg-transparent px-4 py-4 font-mono text-[12px] leading-relaxed text-slate-800 outline-none ring-0 placeholder:text-slate-400"
                    />
                    <div className="flex items-center justify-end gap-2 border-t border-slate-200/80 bg-white/90 px-4 py-2.5">
                        <button
                            type="button"
                            onClick={onResetTableJson}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={onSaveTableJson}
                            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                        >
                            Save table JSON
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
